import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Ban, History, Lock, Monitor, Shield, Users } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/input";
import { Textarea } from "@/components/ui/form-controls";
import { CopyableReference, SupportDetails } from "@/components/ui/copyable-reference";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { Table, TableWrapper } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { usePermission, useSignerPairingAllowed } from "@/hooks/use-permission";
import { useAuthStore } from "@/stores/auth-store";
import {
  describeStepUpOutcome,
  describeStepUpResumeFailure,
  useStepUpResume,
} from "@/hooks/use-step-up-resume";
import { useStepUpLocale } from "@/i18n/use-step-up-locale";
import { PairSignerDevice } from "@/features/admin/pair-signer-device";
import {
  fetchAdminUserDetail,
  suspendAdminUser,
  idempotencyKey,
  updateAdminUserRoles,
  fetchUserSessions,
  fetchUserLoginHistory,
  fetchAdminRoles,
  withStepUpRetry,
  type AdminUser,
  type AdminSession,
  type AuditEvent,
  type AdminRoleDefinition,
  type AdminRoleUpdateRequest,
  type ApiErrorInfo,
} from "@/lib/api";
import type { AppUser, UserRole, UserStatus } from "@/types";
import { ApiFailure, usePlainErrorMessage } from "@/components/data-display/api-failure";
import { useApiQuery } from "@/hooks/use-api";
import { legacyErrorReason } from "@/lib/api-failure";

function toAppUser(u: AdminUser): AppUser {
  const roles = roleNames(u);
  return {
    id: u.principalId,
    name: u.displayName || u.email,
    email: u.email,
    role: (roles.length > 0 ? roles.join(", ") : u.principalType || "Organization Admin") as UserRole,
    groups: u.groupNames ? u.groupNames.split(",") : [],
    mfa: u.mfaMethod || "",
    status: u.status === "ACTIVE" ? "Active" : u.status === "DEACTIVATED" ? "Suspended" : u.status === "EXPIRED" ? "Suspended" : u.status === "INVITED" ? "Invited" : "Active",
    lastActive: u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—",
  };
}

function roleNames(u: AdminUser | null): string[] {
  if (!u?.roleName) return [];
  return u.roleName
    .split(",")
    .map((role) => role.trim())
    .filter(Boolean);
}

/** A role carries approval authority when its catalog entry grants approvals:approve
 * (scope) or approval.approve (permission). The gateway serializes these either as
 * wire values or as enum names, so both spellings are accepted. */
export function isApprovalCapableRole(role: AdminRoleDefinition): boolean {
  const scopes = (role.scopes ?? []).map((scope) => scope.toLowerCase());
  const permissions = (role.permissions ?? []).map((permission) => permission.toLowerCase());
  return (
    scopes.includes("approvals:approve")
    || scopes.includes("approvals_approve")
    || permissions.includes("approval.approve")
    || permissions.includes("approval_approve")
  );
}

function isHumanPrincipal(u: AdminUser | null): boolean {
  if (!u?.principalType) return true;
  return u.principalType.trim().toLowerCase() === "human_user";
}

function sameRoleSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sorted = [...b].sort();
  return [...a].sort().every((role, index) => role === sorted[index]);
}

/** Upper bound for the free-text reason sent with PATCH .../roles. */
const ROLE_CHANGE_REASON_MAX = 500;

interface RoleCatalogState {
  status: "loading" | "ready" | "error";
  roles: AdminRoleDefinition[];
  error?: string;
  reasonCode?: string;
  supportReference?: string;
  capability?: string;
}

interface RoleSaveFeedback {
  kind: "error" | "success";
  message: string;
  supportReference?: string;
  capability?: string;
}

function statusVariant(status: UserStatus): "success" | "warning" | "danger" | "neutral" {
  if (status === "Active") return "success";
  if (status === "Invited") return "warning";
  if (status === "Suspended") return "danger";
  return "neutral";
}

function initials(name: string) {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 text-sm text-ink">{value}</div>
    </div>
  );
}

function formatTimestamp(ts: string | null | undefined): string {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return "—"; }
}

const TH = "px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted";
const TD = "px-4 py-[9px] text-[11.5px] text-ink";

// `useApiQuery` re-runs its effect when `initialData` changes identity, so the
// empty collections are module constants rather than inline `[]` (ALL-5707).
const EMPTY_SESSIONS: AdminSession[] = [];
const EMPTY_EVENTS: AuditEvent[] = [];

/** A failed auxiliary security read, as the two readers report it: the legacy
 * HTTP/network text plus whatever typed reason, support reference and missing
 * capability the server actually returned. */
type SecurityReadFailure = {
  error?: string;
  reasonCode?: string;
  supportReference?: string;
  capability?: string;
};

function securityReadError(raw: SecurityReadFailure): ApiErrorInfo {
  return {
    reasonCode: raw.reasonCode || legacyErrorReason(raw.error) || "NETWORK_ERROR",
    ...(raw.supportReference ? { supportReference: raw.supportReference } : {}),
    ...(raw.capability ? { capability: raw.capability } : {}),
  };
}

function mapSessionsRead(raw: SecurityReadFailure & { sessions: AdminSession[] }) {
  // A failed read is never an empty list: returning the error keeps the tab
  // from presenting a failed observation as a known zero.
  return raw.error
    ? { data: EMPTY_SESSIONS, error: securityReadError(raw) }
    : { data: raw.sessions };
}

function mapHistoryRead(raw: SecurityReadFailure & { events: AuditEvent[] }) {
  return raw.error
    ? { data: EMPTY_EVENTS, error: securityReadError(raw) }
    : { data: raw.events };
}

export default function UserDetailPage() {
  const plainError = usePlainErrorMessage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const canSuspend = usePermission("user:suspend");
  const canPairSigner = useSignerPairingAllowed();
  const canManageRoles = usePermission("role:full");
  const stepUp = useStepUpLocale();
  const [user, setUser] = useState<AppUser | null>(null);
  const [apiUser, setApiUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suspending, setSuspending] = useState(false);
  const [reload, setReload] = useState(0);
  /** A suspend that was refused, or a resumed suspend that could not finish. */
  const [suspendFeedback, setSuspendFeedback] = useState<
    { title?: string; message: string; supportReference?: string; capability?: string } | null
  >(null);
  const [roleDef, setRoleDef] = useState<AdminRoleDefinition | null>(null);
  const [catalog, setCatalog] = useState<RoleCatalogState>({ status: "loading", roles: [] });
  const [catalogReload, setCatalogReload] = useState(0);

  // The two auxiliary security reads are independent sources: each owns its
  // loading, error and retry, so a failed or denied read is recoverable without
  // discarding the user overview or the other tab (ALL-7267). `useApiQuery`
  // owns cancellation and stale-response suppression, including when the
  // route's :id changes while a read is in flight; scoping the fetchers to id
  // starts the next person's page from a new read.
  const sessionsFetcher = useCallback(
    (signal: AbortSignal) => fetchUserSessions(id ?? "", signal),
    [id, reload],
  );
  const historyFetcher = useCallback(
    (signal: AbortSignal) => fetchUserLoginHistory(id ?? "", signal),
    [id, reload],
  );
  const sessionsQuery = useApiQuery(sessionsFetcher, mapSessionsRead, {
    initialData: EMPTY_SESSIONS,
    enabled: Boolean(id),
  });
  const historyQuery = useApiQuery(historyFetcher, mapHistoryRead, {
    initialData: EMPTY_EVENTS,
    enabled: Boolean(id),
  });

  // A suspend parked before a step-up re-authentication (ALL-5021) is completed
  // and replayed here, on the way back to this page. The role change has its own
  // hook inside RoleAssignmentPanel, scoped to its own action.
  const { resuming } = useStepUpResume({
    actions: ["admin.user.suspend"],
    onResuming: () => {
      setSuspendFeedback(null);
      setSuspending(true);
    },
    onResumed: () => {
      setSuspending(false);
      setReload((count) => count + 1);
    },
    onFailed: (failure) => {
      setSuspending(false);
      const described = describeStepUpResumeFailure(failure, stepUp);
      setSuspendFeedback({
        title: described.title,
        message: described.message,
        supportReference: described.supportReference,
        capability: described.capability,
      });
    },
  });

  useEffect(() => {
    let cancelled = false;
    if (!id) { setLoading(false); return; }
    fetchAdminUserDetail(id)
      .then((result) => {
        if (cancelled) return;
        if (result.user) {
          setApiUser(result.user);
          const appUser = toAppUser(result.user);
          setUser(appUser);
        } else {
          setError(result.error || "User not found");
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to connect to the admin API.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, reload]);

  async function handleSuspend() {
    if (!user || !id) return;
    setSuspending(true);
    setSuspendFeedback(null);
    // One key for both attempts: this call and the replay after a step-up
    // re-authentication are the same suspend, not two (ALL-5312 requires the
    // header; ALL-5285 requires the replay to reuse it).
    const key = idempotencyKey("admin-user-suspend");
    const outcome = await withStepUpRetry(() => suspendAdminUser(id, key), {
      action: "admin.user.suspend",
      payload: { userId: id },
      idempotencyKey: key,
      returnPath: `${location.pathname}${location.search}`,
    });
    // The browser is on its way to the identity provider; the resume hook sends
    // it back here and replays this suspend.
    if (outcome.redirected) return;
    if (outcome.result.ok) {
      setUser({ ...user, status: "Suspended" });
      setSuspending(false);
      return;
    }
    const described = describeStepUpOutcome(outcome, stepUp);
    setSuspendFeedback({
      message: described.message,
      supportReference: described.supportReference,
      capability: described.capability,
    });
    setSuspending(false);
  }

  useEffect(() => {
    let cancelled = false;
    setCatalog((current) => ({ ...current, status: "loading" }));
    fetchAdminRoles().then((rolesResult) => {
      if (cancelled) return;
      if (rolesResult.error) {
        setCatalog({
          status: "error",
          roles: [],
          error: rolesResult.error,
          reasonCode: rolesResult.reasonCode,
          supportReference: rolesResult.supportReference,
          capability: rolesResult.capability,
        });
        return;
      }
      setCatalog({ status: "ready", roles: rolesResult.roles });
    });
    return () => { cancelled = true; };
  }, [catalogReload]);

  useEffect(() => {
    if (!apiUser || catalog.status !== "ready") return;
    const assigned = roleNames(apiUser);
    setRoleDef(catalog.roles.find((role) => assigned.includes(role.roleName)) ?? null);
  }, [apiUser, catalog]);

  function handleRolesSaved(nextRoles: string[]) {
    if (!user) return;
    const nextRoleLabel = nextRoles.join(", ");
    setUser({ ...user, role: nextRoleLabel as UserRole });
    setApiUser(apiUser ? { ...apiUser, roleName: nextRoleLabel } : apiUser);
  }

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="spinner" aria-label="Loading user" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="rounded-lg border border-line bg-surface p-8 text-center">
        <h2 className="text-lg font-bold text-ink">{error && legacyErrorReason(error) !== "HTTP_404" && error !== "User not found" ? "Couldn't load this user" : "User not found"}</h2>
        <p className="mt-1 text-sm text-muted">
          {error && legacyErrorReason(error) !== "HTTP_404" && error !== "User not found"
            ? plainError({ reasonCode: legacyErrorReason(error) })
            : `No user exists with ID ${id}.`}
        </p>
        {error && legacyErrorReason(error) !== "HTTP_404" && error !== "User not found" && (
          <p className="mt-1 font-mono text-[11px] text-muted">Code: {legacyErrorReason(error)}</p>
        )}
        <Button className="mt-4" onClick={() => navigate("/admin/users")}>Back to Users</Button>
      </div>
    );
  }

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <a href="#" onClick={(e) => e.preventDefault()} className="text-primary no-underline hover:underline">Identity</a>
        <span className="text-muted" aria-hidden>›</span>
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Users</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">{user.name}</span>
      </nav>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar initials={initials(user.name)} size={56} fontSize={20} />
          <div>
            <h1 className="text-xl font-extrabold text-ink">{user.name}</h1>
            <p className="text-sm text-muted">{user.email} · {user.id}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Badge variant={statusVariant(user.status)}>{user.status}</Badge>
              <Badge variant="neutral" dot={false}>{user.role}</Badge>
              <Badge variant="info" dot={false}>{user.mfa || "No MFA"}</Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canSuspend && (
            <Button variant="secondary" onClick={handleSuspend} loading={suspending} disabled={user.status === "Suspended"}>
              <Ban className="mr-1.5 size-4" /> Suspend
            </Button>
          )}
        </div>
      </div>

      {resuming && (
        <p role="status" className="mb-4 text-xs text-muted">{stepUp.ui["stepUp.resuming"]}</p>
      )}

      {suspendFeedback && (
        <div role="alert" className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-2 text-sm text-danger">
          {suspendFeedback.title && <p className="font-semibold">{suspendFeedback.title}</p>}
          <p>{suspendFeedback.message}</p>
          <SupportDetails
            reference={suspendFeedback.supportReference}
            capability={suspendFeedback.capability}
          />
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" count={undefined}>Overview</TabsTrigger>
          <TabsTrigger value="sessions" count={sessionsQuery.loading || sessionsQuery.error ? undefined : sessionsQuery.data.length}>Sessions</TabsTrigger>
          <TabsTrigger value="login-history" count={historyQuery.loading || historyQuery.error ? undefined : historyQuery.data.length}>Login History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5 space-y-5">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardBody>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="size-4 text-primary" /> Identity
                </CardTitle>
                <div className="mt-3 grid grid-cols-2 gap-x-4">
                  <KV label="User ID" value={user.id} />
                  <KV label="Email" value={user.email} />
                  <KV label="Role" value={user.role} />
                  <KV label="Status" value={<Badge variant={statusVariant(user.status)} size="sm">{user.status}</Badge>} />
                  <KV label="Created" value={apiUser ? formatTimestamp(apiUser.createdAt) : "—"} />
                  <KV label="Last Active" value={user.lastActive} />
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="size-4 text-primary" /> MFA & Security
                </CardTitle>
                <div className="mt-3 grid grid-cols-2 gap-x-4">
                  <KV label="MFA Method" value={user.mfa || "Not configured"} />
                  <KV label="Principal Type" value={apiUser?.principalType || "—"} />
                </div>
              </CardBody>
            </Card>
          </div>

          <PairSignerDevice
            principalId={id ?? ""}
            isHuman={isHumanPrincipal(apiUser)}
            canManage={canPairSigner}
          />

          <Card>
            <CardBody>
              <CardTitle className="flex items-center gap-2">
                <Users className="size-4 text-primary" /> Groups & Permissions
              </CardTitle>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div>
                  <h4 className="mb-2 text-xs font-semibold text-muted">Assigned Groups</h4>
                  {user.groups.length === 0 ? (
                    <p className="text-sm text-muted">Not assigned to any groups.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {user.groups.map((g) => (
                        <Badge key={g} variant="accent" dot={false}>{g}</Badge>
                      ))}
                    </div>
                  )}
                  <Link to="/admin/groups" className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">
                    Manage groups →
                  </Link>
                </div>
                <div>
                  <h4 className="mb-2 text-xs font-semibold text-muted">Role Permissions</h4>
                  <RoleAssignmentPanel
                    userId={id ?? ""}
                    targetUser={apiUser}
                    canManageRoles={canManageRoles}
                    catalog={catalog}
                    onRetryCatalog={() => setCatalogReload((n) => n + 1)}
                    onSaved={handleRolesSaved}
                  />
                  {roleDef ? (
                    <div className="space-y-1">
                      {roleDef.scopes.slice(0, 8).map((scope) => (
                        <div key={scope} className="flex items-center justify-between text-sm">
                          <span className="text-ink font-mono text-xs">{scope}</span>
                        </div>
                      ))}
                      {roleDef.scopes.length > 8 && (
                        <p className="text-xs text-muted">+{roleDef.scopes.length - 8} more scopes</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted">No role definition found.</p>
                  )}
                  <Link to="/admin/roles" className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">
                    View full role catalog →
                  </Link>
                </div>
              </div>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="sessions" className="mt-5">
          <SessionsTab
            sessions={sessionsQuery.data}
            loading={sessionsQuery.loading}
            error={sessionsQuery.error}
            onRetry={sessionsQuery.refresh}
          />
        </TabsContent>

        <TabsContent value="login-history" className="mt-5">
          <LoginHistoryTab
            events={historyQuery.data}
            loading={historyQuery.loading}
            error={historyQuery.error}
            onRetry={historyQuery.refresh}
          />
        </TabsContent>
      </Tabs>

      <div className="mt-6">
        <Button variant="secondary" onClick={() => navigate("/admin/users")} className="gap-1">
          <ArrowLeft className="size-4" /> Back to Users
        </Button>
      </div>
    </div>
  );
}

function RoleAssignmentPanel({
  userId,
  targetUser,
  canManageRoles,
  catalog,
  onRetryCatalog,
  onSaved,
}: {
  userId: string;
  targetUser: AdminUser | null;
  canManageRoles: boolean;
  catalog: RoleCatalogState;
  onRetryCatalog: () => void;
  onSaved: (roles: string[]) => void;
}) {
  const plainError = usePlainErrorMessage();
  const signedInEmail = useAuthStore((s) => s.user?.email ?? null);
  const location = useLocation();
  const stepUp = useStepUpLocale();
  const assignedRoles = roleNames(targetUser);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(assignedRoles);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<RoleSaveFeedback | null>(null);
  const assignedKey = assignedRoles.join(",");

  // A role change parked before a step-up re-authentication (ALL-5021) is
  // completed and replayed here, on the way back to this user's page. Scoped to
  // this panel's own action so the page-level suspend hook never touches it.
  useStepUpResume({
    actions: ["admin.user.roles.update"],
    onResuming: () => {
      setFeedback(null);
      setSaving(true);
    },
    onResumed: (record, outcome) => {
      const replayed = (record.payload as { roles?: unknown }).roles;
      const nextRoles = Array.isArray(replayed) ? replayed.map(String) : selectedRoles;
      setSelectedRoles(nextRoles);
      setReason("");
      onSaved(nextRoles);
      setFeedback({ kind: "success", message: "Roles saved.", supportReference: outcome.supportReference });
      setSaving(false);
    },
    onFailed: (failure) => {
      const described = describeStepUpResumeFailure(failure, stepUp);
      setFeedback({
        kind: "error",
        message: described.message,
        supportReference: described.supportReference,
        capability: described.capability,
      });
      setSaving(false);
    },
  });

  useEffect(() => {
    setSelectedRoles(assignedKey ? assignedKey.split(",") : []);
  }, [assignedKey]);

  const isSelf = Boolean(
    signedInEmail && targetUser?.email && signedInEmail.trim().toLowerCase() === targetUser.email.trim().toLowerCase(),
  );
  const isHuman = isHumanPrincipal(targetUser);
  const changed = !sameRoleSet(selectedRoles, assignedRoles);
  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= ROLE_CHANGE_REASON_MAX;
  const blockedMessage = !isHuman
    ? "Roles can only be assigned to people. Machine and service principals never receive admin or approval roles."
    : isSelf
      ? "You can't change your own roles. Ask another organization admin."
      : null;

  function toggleRole(roleName: string) {
    setFeedback(null);
    setSelectedRoles((current) =>
      current.includes(roleName) ? current.filter((role) => role !== roleName) : [...current, roleName].sort(),
    );
  }

  async function handleSave() {
    if (!userId || selectedRoles.length === 0 || !changed || !reasonOk || blockedMessage) return;
    setSaving(true);
    setFeedback(null);
    const change: AdminRoleUpdateRequest = { userId, roles: selectedRoles, reason: reason.trim() };
    // One key for both attempts: this call and the replay after a step-up
    // re-authentication are the same role change, not two
    // (GatewayRouteScopes.requiresIdempotency covers this PATCH).
    const key = idempotencyKey("admin-role-update");
    const outcome = await withStepUpRetry(
      () => updateAdminUserRoles(change.userId, change.roles, change.reason, key),
      {
        action: "admin.user.roles.update",
        payload: change,
        idempotencyKey: key,
        returnPath: `${location.pathname}${location.search}`,
      },
    );
    // The browser is on its way to the identity provider; this page's resume hook
    // replays the same PATCH on the way back.
    if (outcome.redirected) return;
    const result = outcome.result;
    if (result.ok) {
      const nextRoles = result.roles && result.roles.length > 0 ? result.roles : selectedRoles;
      setSelectedRoles(nextRoles);
      setReason("");
      onSaved(nextRoles);
      setFeedback({
        kind: "success",
        message:
          result.effectiveOn === "NEXT_SIGN_IN"
            ? "Roles saved. They take effect on the user's next sign-in."
            : "Roles saved.",
        supportReference: result.supportReference,
      });
    } else {
      const described = describeStepUpOutcome(outcome, stepUp);
      setFeedback({
        kind: "error",
        message: described.message || "Role update failed",
        supportReference: described.supportReference,
        capability: described.capability,
      });
    }
    setSaving(false);
  }

  if (!canManageRoles) {
    return (
      <p className="mb-4 text-xs text-muted" data-testid="role-assignment-readonly">
        You can view this user's roles. Changing roles requires the role management permission (auth_roles:manage).
      </p>
    );
  }

  return (
    <section
      aria-labelledby="role-assignment-heading"
      className="mb-4 rounded-lg border border-line bg-bg p-3"
    >
      <div id="role-assignment-heading" className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
        Assign roles
      </div>
      {catalog.status === "loading" && <p className="text-sm text-muted">Loading role catalog...</p>}
      {catalog.status === "error" && (
        <div role="alert" className="rounded-md border border-danger bg-danger-soft px-3 py-2 text-xs text-danger">
          The role catalog could not be loaded, so roles can&apos;t be changed right now.{" "}
          {plainError({ reasonCode: catalog.reasonCode || legacyErrorReason(catalog.error) })}
          <span className="mt-1 block font-mono text-[11px]">Code: {catalog.reasonCode || legacyErrorReason(catalog.error)}</span>
          <SupportDetails reference={catalog.supportReference} capability={catalog.capability} />
          <Button size="sm" variant="secondary" className="mt-2" onClick={onRetryCatalog}>
            Retry
          </Button>
        </div>
      )}
      {catalog.status === "ready" && catalog.roles.length === 0 && (
        <p className="text-sm text-muted">No roles are defined for this organization yet.</p>
      )}
      {catalog.status === "ready" && catalog.roles.length > 0 && (
        <>
          {blockedMessage && (
            <p className="mb-2 rounded-md border border-line bg-surface px-3 py-2 text-xs text-muted">{blockedMessage}</p>
          )}
          <fieldset className="grid gap-2" disabled={Boolean(blockedMessage) || saving}>
            <legend className="sr-only">Roles for {targetUser?.email ?? "this user"}</legend>
            {catalog.roles.map((role) => {
              const approval = isApprovalCapableRole(role);
              return (
                <label key={role.roleName} className="flex items-start gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selectedRoles.includes(role.roleName)}
                    onChange={() => toggleRole(role.roleName)}
                  />
                  <span>
                    <span className="font-semibold">{role.displayName || role.roleName}</span>
                    <span className="ml-2 font-mono text-[11px] text-muted">{role.roleName}</span>
                    {approval && (
                      <span className="mt-0.5 block text-[11px] text-warning">
                        Approval authority. Granted only to this named person, by an admin signed in with MFA; never by domain or to a machine.
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </fieldset>
          <div className="mt-3">
            <Field
              label="Reason for change"
              htmlFor="role-change-reason"
              required
              hint="Sent with the change request. The audit receipt records who changed which roles, and when."
            >
              <Textarea
                id="role-change-reason"
                rows={2}
                maxLength={ROLE_CHANGE_REASON_MAX}
                value={reason}
                disabled={Boolean(blockedMessage) || saving}
                onChange={(event) => {
                  setFeedback(null);
                  setReason(event.target.value);
                }}
                placeholder="e.g. Approver for the treasury desk from 1 Oct"
              />
            </Field>
          </div>
          {feedback && (
            <div
              role={feedback.kind === "error" ? "alert" : "status"}
              className={
                feedback.kind === "error"
                  ? "mt-3 rounded-md border border-danger bg-danger-soft px-3 py-2 text-xs text-danger"
                  : "mt-3 rounded-md border border-success bg-success-soft px-3 py-2 text-xs text-success"
              }
            >
              {feedback.message}
              {feedback.kind === "error" ? (
                <SupportDetails reference={feedback.supportReference} capability={feedback.capability} />
              ) : (
                <CopyableReference reference={feedback.supportReference} compact className="mt-1.5" />
              )}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              loading={saving}
              disabled={Boolean(blockedMessage) || selectedRoles.length === 0 || !changed || !reasonOk}
            >
              Save roles
            </Button>
            <span className="text-xs text-muted">
              {selectedRoles.length === 0 ? "Select at least one role." : "Applies on the user's next sign-in."}
            </span>
          </div>
        </>
      )}
    </section>
  );
}

function SessionsTab({
  sessions,
  loading,
  error,
  onRetry,
}: {
  sessions: AdminSession[];
  loading: boolean;
  error: ApiErrorInfo | null;
  onRetry: () => void;
}) {
  return (
    <Card>
      <CardBody>
        <CardTitle className="flex items-center gap-2">
          <Monitor className="size-4 text-primary" /> Active Sessions
        </CardTitle>
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-muted">Loading sessions...</div>
        ) : error ? (
          <ApiFailure error={error} subject="this user's active sessions" onRetry={onRetry} />
        ) : (
          <TableWrapper className="mt-3">
            <Table>
              <thead>
                <tr className="border-b border-line bg-bg">
                  <th className={TH}>Session</th>
                  <th className={TH}>Auth Method</th>
                  <th className={TH}>MFA</th>
                  <th className={TH}>Assurance</th>
                  <th className={TH}>Channel</th>
                  <th className={TH}>Created</th>
                  <th className={TH}>Last Seen</th>
                  <th className={TH}>Idle Expires</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.sessionIdSuffix} className="border-b border-line last:border-b-0">
                    <td className={`${TD} font-mono`}>...{s.sessionIdSuffix}</td>
                    <td className={TD}>{s.authMethod || "—"}</td>
                    <td className={TD}>{s.mfaPresent ? "Yes" : "No"}</td>
                    <td className={TD}>{s.assuranceLevel || "—"}</td>
                    <td className={TD}>{s.channel || "—"}</td>
                    <td className={TD}>{formatTimestamp(s.createdAt)}</td>
                    <td className={TD}>{formatTimestamp(s.lastSeenAt)}</td>
                    <td className={TD}>{formatTimestamp(s.idleExpiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {sessions.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted">No session data available.</div>
            )}
          </TableWrapper>
        )}
      </CardBody>
    </Card>
  );
}

function OutcomePill({ outcome }: { outcome: string }) {
  const variant = outcome === "ALLOW" ? "success" : outcome === "DENY" ? "danger" : "neutral";
  return <Badge variant={variant} size="sm" dot={false}>{outcome}</Badge>;
}

function LoginHistoryTab({
  events,
  loading,
  error,
  onRetry,
}: {
  events: AuditEvent[];
  loading: boolean;
  error: ApiErrorInfo | null;
  onRetry: () => void;
}) {
  return (
    <Card>
      <CardBody>
        <CardTitle className="flex items-center gap-2">
          <History className="size-4 text-primary" /> Login History
        </CardTitle>
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-muted">Loading login history...</div>
        ) : error ? (
          <ApiFailure error={error} subject="this user's login history" onRetry={onRetry} />
        ) : (
          <TableWrapper className="mt-3">
            <Table>
              <thead>
                <tr className="border-b border-line bg-bg">
                  <th className={TH}>Event</th>
                  <th className={TH}>Outcome</th>
                  <th className={TH}>Auth Method</th>
                  <th className={TH}>Assurance</th>
                  <th className={TH}>MFA</th>
                  <th className={TH}>Time</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.eventId} className="border-b border-line last:border-b-0">
                    <td className={TD}>{e.eventType || "—"}</td>
                    <td className="px-4 py-[9px]"><OutcomePill outcome={e.outcome || "—"} /></td>
                    <td className={TD}>{e.authMethod || "—"}</td>
                    <td className={TD}>{e.assuranceLevel || "—"}</td>
                    <td className={TD}>{e.mfaPresent ? "Yes" : "No"}</td>
                    <td className={TD}>{formatTimestamp(e.occurredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {events.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted">No login history available.</div>
            )}
          </TableWrapper>
        )}
      </CardBody>
    </Card>
  );
}
